import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import { clerkDarkBase } from './theme/clerkDarkBase';
import { isEmbedPath, EmbedProvider } from './context/EmbedContext';

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const isEmbed = isEmbedPath();

if (!isEmbed && !clerkPubKey) {
  console.error('Missing REACT_APP_CLERK_PUBLISHABLE_KEY in .env');
}

function ClerkThemedProvider({ children }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const variables = isDark
    ? {
        colorPrimary: '#14A085',
        colorPrimaryForeground: '#ffffff',
        colorText: '#E8F4F2',
        colorTextSecondary: '#A3BEBA',
        colorBackground: '#1c2e2c',
        colorForeground: '#E8F4F2',
        colorInput: '#243332',
        colorInputForeground: '#E8F4F2',
        colorNeutral: '#e8f4f2',
        fontFamily: '"DM Sans", sans-serif',
        borderRadius: '0.75rem',
      }
    : {
        colorPrimary: '#0D7377',
        colorText: '#1A2E2E',
        colorTextSecondary: '#4A6A6A',
        colorBackground: '#FFFFFF',
        fontFamily: '"DM Sans", sans-serif',
        borderRadius: '0.75rem',
      };

  const appearance = isDark
    ? {
        baseTheme: clerkDarkBase,
        variables,
        elements: {
          logoImage: {
            filter: 'brightness(0) invert(1)',
          },
        },
      }
    : {
        variables,
      };

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      appearance={appearance}
    >
      {children}
    </ClerkProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const queryClient = new QueryClient();

const appTree = (
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);

root.render(
  <React.StrictMode>
    <ThemeProvider>
      {isEmbed ? (
        <EmbedProvider>
          <BrowserRouter basename="/embed">
            {appTree}
          </BrowserRouter>
        </EmbedProvider>
      ) : (
        <ClerkThemedProvider>
          <BrowserRouter>
            {appTree}
          </BrowserRouter>
        </ClerkThemedProvider>
      )}
    </ThemeProvider>
  </React.StrictMode>,
);

reportWebVitals();
